import * as React from "react"
import * as Antd from "antd"
import styles from "./ValidateAccountForm.module.scss"

import { AppRoutes } from "@constants/AppRoutes.ts"
import { AppMessage } from "@others/AppMessage.ts"
import { useTranslation } from "react-i18next"
import { IGetErrorApiDetail } from "@interfaces/IGetErrorApiDetail.ts"
import { useLocation, useNavigate } from "react-router-dom"
import { IExternalMembershipApplies } from "@pages/ExternalMembershipAppliesFlow/ExternalMembershipAppliesFlow.tsx"

import DvInput from "@components/dataEntry/DvInput/DvInput.tsx"
import DsButton from "@components/general/DsButton/DsButton.tsx"
import DvAntdForm from "@components/others/DvAntdForm/DvAntdForm.tsx"
import authService from "@services/authService/authService.ts"
import DvAntdFormItem from "@components/others/DvAntdFormItem/DvAntdFormItem.tsx"
import validationRules from "@others/validationRules.ts"

type FieldType = {
	email?: string
}

interface IProps {
	className?: string
}

const ValidateAccountForm: React.FC<IProps> = (props) => {
	const navigate = useNavigate()
	const location = useLocation()

	const { t } = useTranslation()

	const [form] = Antd.Form.useForm<FieldType>()
	const [isCheckingEmail, setIsCheckingEmail] = React.useState(false)

	// membership external apply flow
	const externalMembershipApplies = location.state
		?.externalMembershipApplies as IExternalMembershipApplies | undefined

	const onFinish = ({ email }: FieldType) => {
		setIsCheckingEmail(true)

		authService
			.checkEmail({ email })
			.then((response) => {
				if (response.accountExists) {
					AppMessage.success(t("message_account_exists"))

					navigate(AppRoutes.login, {
						replace: true,
						state: { email, externalMembershipApplies },
					})
				} else {
					navigate(AppRoutes.signup, {
						replace: true,
						state: { email, externalMembershipApplies },
					})
				}
			})
			.catch(({ code }: IGetErrorApiDetail) => {
				switch (code) {
					case "EMAIL_REQUIRED":
						form.setFields([
							{
								name: "email",
								errors: [t("please_enter_your_email")],
							},
						])
						break
					case "INCORRECT_EMAIL_FORMAT":
						form.setFields([
							{
								name: "email",
								errors: [t("your_email_is_not_valid")],
							},
						])
						break
					default:
						AppMessage.error(t("something_went_wrong"))
				}
			})
			.finally(() => {
				setIsCheckingEmail(false)
			})
	}

	return (
		<DvAntdForm
			form={form}
			className={`${styles.validate_account_form} ${props.className || ""}`}
			name="basic"
			initialValues={{ remember: true }}
			onFinish={onFinish}
		>
			<div className={styles.top}>
				<header className={styles.header}>
					{t("instructions_to_validate_account")}
				</header>

				<main className={styles.body}>
					<DvAntdFormItem<FieldType>
						className={styles.form_item}
						label={t("email")}
						name="email"
						validateDebounce={1500}
						rules={[
							validationRules.requiredFieldRule(
								t("please_enter_your_email")
							),
							validationRules.emailRule(
								t("your_email_is_not_valid")
							),
						]}
					>
						<DvInput placeholder={t("insert_your_email")} />
					</DvAntdFormItem>
				</main>
			</div>

			<div className={styles.footer}>
				<DsButton
					className={styles.action_button}
					htmlType="submit"
					loading={isCheckingEmail}
				>
					{t("continue")}
				</DsButton>

				<DsButton
					type="secondary"
					className={styles.action_button}
					onClick={() => {
						navigate(AppRoutes.login, {
							replace: true,
							state: { externalMembershipApplies },
						})
					}}
				>
					{t("back")}
				</DsButton>
			</div>
		</DvAntdForm>
	)
}

export default React.memo(ValidateAccountForm)
